import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faUsers, 
  faBlog, 
  faBriefcase, 
  faProjectDiagram, 
  faShoppingCart,
  faEye,
  faChartLine,
  faClock,
  faTasks,
  faCheckCircle,
  faServer,
  faCogs,
  faDatabase,
  faChartBar
} from '@fortawesome/free-solid-svg-icons'
import '../Css/AdminDashboard.css'
import { API_URL } from '../utils/Function'

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalBlogs: 0,
    totalCareers: 0,
    totalProjects: 0,
    totalProducts: 0,
    totalApplications: 0,
    pendingApplications: 0,
    totalServices: 0
  })
  const [loading, setLoading] = useState(true)
  const [summary, setSummary] = useState({
    publishedBlogs: 0,
    activeProjects: 0,
    openPositions: 0,
    completedProjects: 0
  })
  const [systemInfo, setSystemInfo] = useState({
    totalCategories: 0,
    activeServices: 0,
    featuredProducts: 0,
    systemStatus: 'Operational'
  })

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token')
      
      // Fetch all data in parallel
      const [
        usersRes, 
        blogsRes, 
        careersRes, 
        projectsRes, 
        productsRes, 
        applicationsRes,
        servicesRes,
        categoriesRes
      ] = await Promise.all([
        fetch(`${API_URL}/user.php/get?limit=1`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/blog.php/get`),
        fetch(`${API_URL}/career.php/get`),
        fetch(`${API_URL}/project.php/get`),
        fetch(`${API_URL}/product.php/get`),
        fetch(`${API_URL}/career_application.php/get`),
        fetch(`${API_URL}/service.php/get`),
        fetch(`${API_URL}/category.php/get`)
      ])

      const usersData = await usersRes.json()
      const blogsData = await blogsRes.json()
      const careersData = await careersRes.json()
      const projectsData = await projectsRes.json()
      const productsData = await productsRes.json()
      const applicationsData = await applicationsRes.json()
      const servicesData = await servicesRes.json()
      const categoriesData = await categoriesRes.json()

      if (usersData.success && blogsData.success && careersData.success && 
          projectsData.success && productsData.success && applicationsData.success &&
          servicesData.success && categoriesData.success) {
        
        const pendingApps = applicationsData.applications?.filter(app => app.status === 'pending') || []
        const publishedBlogs = blogsData.blogs?.filter(blog => blog.status === 'published') || []
        const activeProjects = projectsData.projects?.filter(proj => proj.status === 'in-progress') || []
        const completedProjects = projectsData.projects?.filter(proj => proj.status === 'completed') || []
        const openCareers = careersData.careers?.filter(career => career.status === 'open') || []
        const activeServices = servicesData.services?.filter(service => service.is_active) || []
        const featuredProducts = productsData.products?.filter(product => product.is_featured) || []

        setStats({
          totalUsers: usersData.pagination?.total || usersData.users?.length || 0,
          totalBlogs: blogsData.pagination?.total || blogsData.blogs?.length || 0,
          totalCareers: careersData.pagination?.total || careersData.careers?.length || 0,
          totalProjects: projectsData.pagination?.total || projectsData.projects?.length || 0,
          totalProducts: productsData.pagination?.total || productsData.products?.length || 0,
          totalApplications: applicationsData.pagination?.total || applicationsData.applications?.length || 0,
          pendingApplications: pendingApps.length,
          totalServices: servicesData.pagination?.total || servicesData.services?.length || 0
        })

        setSummary({
          publishedBlogs: publishedBlogs.length,
          activeProjects: activeProjects.length,
          openPositions: openCareers.length,
          completedProjects: completedProjects.length
        })

        setSystemInfo({
          totalCategories: categoriesData.categories?.length || 0,
          activeServices: activeServices.length,
          featuredProducts: featuredProducts.length,
          systemStatus: 'Operational'
        })
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      setSystemInfo(prev => ({
        ...prev,
        systemStatus: 'Issues Detected'
      }))
    } finally {
      setLoading(false)
    }
  }

  const StatCard = ({ icon, title, value, color, trend }) => (
    <div className={`stat-card stat-card-${color}`}>
      <div className="stat-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="stat-info">
        <h3>{value}</h3>
        <p>{title}</p>
        {trend && (
          <span className={`trend ${trend.type}`}>
            {trend.type === 'up' ? '↑' : '↓'} {trend.value}%
          </span>
        )}
      </div>
    </div>
  )

  const SystemCard = ({ icon, title, value, description, status }) => (
    <div className="system-card">
      <div className="system-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="system-content">
        <h4>{title}</h4>
        <div className="system-value">{value}</div>
        <p className="system-description">{description}</p>
        {status && (
          <span className={`system-status ${status.toLowerCase()}`}>
            {status}
          </span>
        )}
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-spinner">
          <FontAwesomeIcon icon={faClock} spin /> Loading Dashboard...
        </div>
      </div>
    )
  }

  return (
    <div className="page-container">
      {/* Dashboard Header without Real-time Clock */}
      <div className="dashboard-header">
        <h1>Dashboard Overview</h1>
        <p>Welcome back! Here's your complete system overview.</p>
      </div>

      {/* Main Statistics Grid */}
      <div className="stats-grid">
        <StatCard 
          icon={faUsers} 
          title="Total Users" 
          value={stats.totalUsers} 
          color="blue"
        />
        <StatCard 
          icon={faBlog} 
          title="Total Blogs" 
          value={stats.totalBlogs} 
          color="green"
        />
        <StatCard 
          icon={faBriefcase} 
          title="Career Posts" 
          value={stats.totalCareers} 
          color="purple"
        />
        <StatCard 
          icon={faProjectDiagram} 
          title="Projects" 
          value={stats.totalProjects} 
          color="orange"
        />
        <StatCard 
          icon={faShoppingCart} 
          title="Products" 
          value={stats.totalProducts} 
          color="red"
        />
        <StatCard 
          icon={faEye} 
          title="Pending Applications" 
          value={stats.pendingApplications} 
          color="yellow"
        />
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        <div className="summary-card">
          <h4>Published Blogs</h4>
          <div className="summary-value">{summary.publishedBlogs}</div>
          <div className="summary-change positive">
            <FontAwesomeIcon icon={faCheckCircle} /> Live
          </div>
        </div>
        <div className="summary-card">
          <h4>Active Projects</h4>
          <div className="summary-value">{summary.activeProjects}</div>
          <div className="summary-change positive">
            <FontAwesomeIcon icon={faTasks} /> In Progress
          </div>
        </div>
        <div className="summary-card">
          <h4>Completed Projects</h4>
          <div className="summary-value">{summary.completedProjects}</div>
          <div className="summary-change positive">
            <FontAwesomeIcon icon={faCheckCircle} /> Done
          </div>
        </div>
        <div className="summary-card">
          <h4>Open Positions</h4>
          <div className="summary-value">{summary.openPositions}</div>
          <div className="summary-change positive">
            <FontAwesomeIcon icon={faBriefcase} /> Hiring
          </div>
        </div>
      </div>

      {/* System Information Section */}
      <div className="dashboard-section">
        <h2>
          <FontAwesomeIcon icon={faServer} /> System Information
        </h2>
        <div className="system-grid">
          <SystemCard 
            icon={faDatabase}
            title="Total Categories"
            value={systemInfo.totalCategories}
            description="Available content categories"
          />
          <SystemCard 
            icon={faCogs}
            title="Active Services"
            value={systemInfo.activeServices}
            description="Currently active services"
          />
          <SystemCard 
            icon={faShoppingCart}
            title="Featured Products"
            value={systemInfo.featuredProducts}
            description="Products marked as featured"
          />
          <SystemCard 
            icon={faChartBar}
            title="System Status"
            value="Online"
            description="Overall system health"
            status={systemInfo.systemStatus}
          />
        </div>
      </div>

      {/* Quick Stats Overview */}
      <div className="dashboard-section">
        <h2>
          <FontAwesomeIcon icon={faChartLine} /> Quick Stats Overview
        </h2>
        <div className="quick-stats">
          <div className="quick-stat-item">
            <div className="quick-stat-icon">
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <div className="quick-stat-content">
              <span className="quick-stat-label">Total Team Members</span>
              <span className="quick-stat-value">{stats.totalUsers}</span>
            </div>
          </div>
          <div className="quick-stat-item">
            <div className="quick-stat-icon">
              <FontAwesomeIcon icon={faProjectDiagram} />
            </div>
            <div className="quick-stat-content">
              <span className="quick-stat-label">Active Development</span>
              <span className="quick-stat-value">{summary.activeProjects} Projects</span>
            </div>
          </div>
          <div className="quick-stat-item">
            <div className="quick-stat-icon">
              <FontAwesomeIcon icon={faBriefcase} />
            </div>
            <div className="quick-stat-content">
              <span className="quick-stat-label">Career Opportunities</span>
              <span className="quick-stat-value">{summary.openPositions} Open</span>
            </div>
          </div>
          <div className="quick-stat-item">
            <div className="quick-stat-icon">
              <FontAwesomeIcon icon={faBlog} />
            </div>
            <div className="quick-stat-content">
              <span className="quick-stat-label">Content Published</span>
              <span className="quick-stat-value">{summary.publishedBlogs} Articles</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}